import type Account from '@/data/account/model';
import { getCurrentPlan } from '@/data/account/selectors/currentPlan';
import { getMasterPlan } from '@/data/account/selectors/masterPlan';
import type Plan from '@/data/plan/model';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import type { WiwState } from '@/store';

import { createSelector } from '@reduxjs/toolkit';

const filterAccountPlan = (account: Account | undefined, currentPlan: Plan | null, masterPlan: Plan | null): Plan => {
  return (account?.isChild() ? masterPlan : currentPlan) as unknown as Plan;
};

const getPropsOrStateAccount = (state: WiwState, props?: { account?: Account }): Account | undefined => {
  return props?.account ? props.account : getAuthAccount(state);
};

export const getAccountPlan = createSelector(
  [getPropsOrStateAccount, getCurrentPlan, getMasterPlan],
  filterAccountPlan,
);

export function mustGetAccountPlan(state: WiwState): Plan {
  const maybePlan = getAccountPlan(state);
  if (!maybePlan) {
    throw new Error('no account plan');
  }
  return maybePlan;
}

/**
 * Determines if the failed payment banner should be displayed.
 * Used by other banners to hide themselves when the failed payment banner is showing.
 */
export const shouldShowFailedPaymentBanner = createSelector(
  [getAuthUser, getAuthAccount, getAccountPlan],
  (user, account, accountPlan) => {
    // Only show to admins
    if (!user.isAdmin()) {
      return false;
    }

    // Skip trials - they haven't failed, they just haven't paid yet
    if (accountPlan?.isTrial() && account.isActive()) {
      return false;
    }

    // Show banner for 1-3 failed payment attempts (before account gets deactivated at 4)
    return account.credit_attempts > 0 && account.credit_attempts <= 3;
  },
);
